import {crc32} from '@allex/crc32'
import {hasMatch, score as fzyScore} from 'fzy.js'

export interface Icon {
  type: string
  id?: string
  url?: string
  alt?: string
}

export interface ItemData {
  title: string
  priority: number
  group: string
  subtitle?: string
  matchFields?: readonly string[]
  typeahead?: string
  hint?: string
  icon?: Icon
  readonly path?: string
}

export interface QueryScopeData {
  type: string
  id: string
}

export interface QueryData {
  text: string
  mode: string
  scope: QueryScopeData
  subjectId?: string
  returnTo?: string
}

export interface QueryModeData {
  character: string
  scopeTypes?: readonly string[]
}

export type QueryParameterEntry = [name: string, value: string]

export function commandPaletteProviderQueryKey({
  provider,
  version,
  viewer,
  partition,
  scope,
  parameters,
}: {
  provider: string
  version: number
  viewer: string
  partition: string
  scope: QueryScopeData
  parameters: readonly QueryParameterEntry[]
}) {
  return [
    'command-palette-provider',
    provider,
    version,
    viewer,
    partition,
    getQueryScopeType(scope.type),
    scope.id,
    parameters,
  ] as const
}

const matchAllModes = '*'
const defaultOpenParameterNames = new Set([
  'command_palette_open',
  'command_query',
  'command_mode',
  'clear_command_scope',
])

export function getQueryModeCharacter(character: string): string {
  return character === matchAllModes ? '' : character
}

export function queryModeSupportsScope(scopeTypes: readonly string[] | undefined, scopeType: string): boolean {
  return !scopeTypes || scopeTypes.includes(scopeType)
}

export function commandPaletteProviderSupportsQuery(
  provider: {supportedModes: readonly string[]; supportedScopeTypes?: readonly string[]},
  query: Pick<QueryData, 'mode' | 'scope'>,
): boolean {
  return (
    (provider.supportedModes.includes(query.mode) || provider.supportedModes.includes('*')) &&
    (!provider.supportedScopeTypes?.length ||
      provider.supportedScopeTypes.includes(getQueryScopeType(query.scope.type)))
  )
}

export function queryModeMatchesInput(character: string, inputCharacter: string): boolean {
  return character === matchAllModes || character === inputCharacter
}

export function isQueryModeActive(mode: QueryModeData, scopeType: string, inputCharacter: string): boolean {
  return queryModeSupportsScope(mode.scopeTypes, scopeType) && queryModeMatchesInput(mode.character, inputCharacter)
}

export function findQueryModeIndex(inputValue: string, scopeType: string, modes: readonly QueryModeData[]): number {
  const firstCharacter = inputValue.substring(0, 1)

  return modes.findIndex(
    mode =>
      isQueryModeActive(mode, scopeType, firstCharacter) && getQueryModeCharacter(mode.character) === firstCharacter,
  )
}

export function getQueryScopeType(resourceType?: string): string {
  switch (resourceType) {
    case undefined:
      return ''
    case 'User':
    case 'Organization':
      return 'owner'
    case 'Repository':
      return 'repository'
    case 'Issue':
      return 'issue'
    case 'PullRequest':
      return 'pull_request'
    case 'Discussion':
      return 'discussion'
    case 'MemexProject':
      return 'memex_project'
    default:
      return resourceType
  }
}

export function findQueryMode<T extends QueryModeData>(
  inputValue: string,
  scopeType: string,
  modes: readonly T[],
): T | undefined {
  return modes[findQueryModeIndex(inputValue, scopeType, modes)] ?? modes.find(mode => mode.character === matchAllModes)
}

export function removeQueryModePrefix(inputValue: string, modeCharacter: string): string {
  return modeCharacter && inputValue.startsWith(modeCharacter) ? inputValue.substring(1) : inputValue
}

export function normalizeQueryText(inputValue: string, modeCharacter: string): string {
  return removeQueryModePrefix(inputValue, modeCharacter).trimStart()
}

export function getQueryModeTransition(inputValue: string, modeCharacter: string, enabled: boolean): string {
  if (!modeCharacter) {
    return inputValue
  }

  if (!enabled) {
    return removeQueryModePrefix(inputValue, modeCharacter)
  }

  return inputValue.startsWith(modeCharacter) ? inputValue : `${modeCharacter}${inputValue}`
}

export function buildQueryPath(query: Pick<QueryData, 'text' | 'mode' | 'scope'>, queryText = query.text): string {
  return `scope:${query.scope.type}-${query.scope.id}/mode:${query.mode}/query:${queryText}`
}

export function queryHasScope(scope: QueryScopeData): boolean {
  return scope.id !== ''
}

export function isQueryBlank(text: string): boolean {
  return text.trim().length === 0
}

export function queryHasSameScope(first: QueryScopeData, second: QueryScopeData): boolean {
  return first.id === second.id
}

// Owner and repository names in GitHub paths are case-insensitive.
export function isPathWithinScope(path: string, scopePath: string): boolean {
  const normalizedPath = path.toLowerCase()
  const normalizedScopePath = scopePath.toLowerCase()
  return normalizedPath === normalizedScopePath || normalizedPath.startsWith(`${normalizedScopePath}/`)
}

export function getQueryParameterEntries(query: QueryData): QueryParameterEntry[] {
  const entries: QueryParameterEntry[] = []

  if (!isQueryBlank(query.text)) {
    entries.push(['q', query.text])
  }

  if (queryHasScope(query.scope)) {
    entries.push(['scope', query.scope.id])
  }

  if (query.mode) {
    entries.push(['mode', query.mode])
  }

  if (query.returnTo) {
    entries.push(['return_to', query.returnTo])
  }

  if (query.subjectId) {
    entries.push(['subject', query.subjectId])
  }

  return entries
}

export function removeDefaultOpenQueryParameters(entries: readonly QueryParameterEntry[]): QueryParameterEntry[] {
  return entries.filter(([name]) => !defaultOpenParameterNames.has(name))
}

export function getItemMatchingFields(item: {
  title: string
  matchFields?: readonly string[] | null
}): readonly string[] {
  return item.matchFields ? item.matchFields : [item.title]
}

export function getDefaultItemKey(item: Pick<ItemData, 'title' | 'group' | 'subtitle' | 'matchFields'>): string {
  return `${item.title}-${item.group}-${item.subtitle}-${item.matchFields?.join('-')}`
}

export function getItemId(key: string): string {
  return crc32(key).toString()
}

export function calculateItemScore(matchingFields: readonly string[], queryText: string): number {
  const scores = matchingFields.map(field => (hasMatch(queryText, field) ? fzyScore(queryText, field) : -Infinity))
  return Math.max(...scores)
}

export function filterItemsByScore<T>(
  items: T[],
  isBlank: boolean,
  calculateScore: (item: T) => number,
  minScore?: number,
): T[]
export function filterItemsByScore<T>(
  items: readonly T[],
  isBlank: boolean,
  calculateScore: (item: T) => number,
  minScore?: number,
): readonly T[]
export function filterItemsByScore<T>(
  items: readonly T[],
  isBlank: boolean,
  calculateScore: (item: T) => number,
  minScore = 0,
): readonly T[] {
  if (isBlank) {
    return items
  }

  const matchingItems: T[] = []
  for (const item of items) {
    if (calculateScore(item) > minScore) {
      matchingItems.push(item)
    }
  }

  return matchingItems
}

export function getStaticItemPriority(itemCount: number, index: number): number {
  return itemCount - index
}
